import { openDB, IDBPDatabase } from 'idb';

import { ConversationResource } from '@/types';
import {
  decryptData,
  encryptData,
  generateEncryptionKey,
} from '@/lib/utils/encryption';

const DB_NAME = 'jaqln-storage';
const DB_VERSION = 1;
const CONVERSATIONS_STORE = 'conversations';
const METADATA_STORE = 'conversation-metadata';

interface ConversationMetadata {
  id: string;
  title: string;
  createdAt: Date;
  updatedAt: Date;
}

interface StoredConversation {
  id: string;
  encryptedContent: string;
  version: number;
}

class StorageService {
  private db: IDBPDatabase | null = null;
  private encryptionKey: CryptoKey | null = null;
  private initializationPromise: Promise<void> | null = null;
  private isInitialized = false;
  private autoInitializeTimer: NodeJS.Timeout | null = null;

  constructor() {
    if (typeof window !== 'undefined') {
      // Try to initialize immediately if credentials exist
      const userId = localStorage.getItem('userId');
      const sessionId = localStorage.getItem('sessionId');
      if (userId && sessionId) {
        this.initialize(userId, sessionId).catch(console.error);
      }

      // Set up periodic check for database health
      this.autoInitializeTimer = setInterval(() => {
        if (!this.isInitialized && this.db === null) {
          const userId = localStorage.getItem('userId');
          const sessionId = localStorage.getItem('sessionId');
          if (userId && sessionId) {
            this.initialize(userId, sessionId).catch(console.error);
          }
        }
      }, 30000);
    }
  }

  async initialize(userId: string, sessionId: string) {
    // If already initialized successfully, return early
    if (this.isInitialized && this.db) {
      return;
    }

    // Store credentials for recovery
    localStorage.setItem('userId', userId);
    localStorage.setItem('sessionId', sessionId);

    // If already initializing, wait for that to complete
    if (this.initializationPromise) {
      try {
        await this.initializationPromise;
        return;
      } catch (error) {
        console.error(
          '[StorageService] Existing initialization failed:',
          error
        );
      }
    }

    this.initializationPromise = (async () => {
      try {
        // Close existing connection if any
        if (this.db) {
          this.db.close();
          this.db = null;
        }

        // Open new connection
        this.db = await openDB(DB_NAME, DB_VERSION, {
          upgrade(db) {
            // Store for encrypted conversation content
            if (!db.objectStoreNames.contains(CONVERSATIONS_STORE)) {
              db.createObjectStore(CONVERSATIONS_STORE, { keyPath: 'id' });
            }

            // Store for fast-access metadata
            if (!db.objectStoreNames.contains(METADATA_STORE)) {
              const store = db.createObjectStore(METADATA_STORE, {
                keyPath: 'id',
              });
              store.createIndex(`${METADATA_STORE}_createdAt`, 'createdAt');
            }
          },
          blocked() {
            console.warn(
              '[StorageService] Database blocked, another version is open'
            );
          },
          blocking() {
            console.warn('[StorageService] Database blocking older version');
          },
          terminated: () => {
            console.error('[StorageService] Database terminated unexpectedly');
            this.db = null;
            this.isInitialized = false;
          },
        });

        // Generate new encryption key
        this.encryptionKey = await generateEncryptionKey(userId, sessionId);
        this.isInitialized = true;
        console.log('[StorageService] Successfully initialized');
      } catch (error) {
        console.error('[StorageService] Failed to initialize:', error);
        if (this.db) {
          this.db.close();
        }
        this.db = null;
        this.encryptionKey = null;
        this.isInitialized = false;
        throw error;
      } finally {
        this.initializationPromise = null;
      }
    })();

    await this.initializationPromise;
  }

  private async ensureInitialized() {
    if (!this.isInitialized || !this.db) {
      const userId = localStorage.getItem('userId');
      const sessionId = localStorage.getItem('sessionId');

      if (userId && sessionId) {
        await this.initialize(userId, sessionId);
      }

      if (!this.isInitialized || !this.db) {
        throw new Error(
          'Storage not initialized. Please ensure you are logged in.'
        );
      }
    }
  }

  async storeConversation(conversation: ConversationResource) {
    await this.ensureInitialized();

    const metadata: ConversationMetadata = {
      id: conversation.id,
      title: conversation.title,
      createdAt: new Date(conversation.createdAt),
      updatedAt: new Date(),
    };

    const encryptedContent = await encryptData(
      conversation,
      this.encryptionKey!
    );

    const storedConversation: StoredConversation = {
      id: conversation.id,
      encryptedContent,
      version: 1,
    };

    const tx = this.db!.transaction(
      [CONVERSATIONS_STORE, METADATA_STORE],
      'readwrite'
    );
    await tx.objectStore(CONVERSATIONS_STORE).put(storedConversation);
    await tx.objectStore(METADATA_STORE).put(metadata);
    await tx.done;
  }

  async getConversation(id: string): Promise<ConversationResource | null> {
    await this.ensureInitialized();

    const stored = await this.db!.get(CONVERSATIONS_STORE, id);
    if (!stored) return null;

    return decryptData(stored.encryptedContent, this.encryptionKey!);
  }

  async listConversations(
    limit: number = 50,
    offset: number = 0
  ): Promise<ConversationMetadata[]> {
    await this.ensureInitialized();

    const tx = this.db!.transaction(METADATA_STORE, 'readonly');
    const index = tx.store.index(`${METADATA_STORE}_createdAt`);

    const conversations: ConversationMetadata[] = [];
    let cursor = await index.openCursor(null, 'prev');

    let count = 0;
    while (cursor && count < offset + limit) {
      if (count >= offset) {
        conversations.push(cursor.value);
      }
      cursor = await cursor.continue();
      count++;
    }

    return conversations;
  }

  async deleteConversation(id: string) {
    await this.ensureInitialized();

    const tx = this.db!.transaction(
      [CONVERSATIONS_STORE, METADATA_STORE],
      'readwrite'
    );
    await tx.objectStore(CONVERSATIONS_STORE).delete(id);
    await tx.objectStore(METADATA_STORE).delete(id);
    await tx.done;
  }

  async clearAll() {
    await this.ensureInitialized();

    const tx = this.db!.transaction(
      [CONVERSATIONS_STORE, METADATA_STORE],
      'readwrite'
    );
    await tx.objectStore(CONVERSATIONS_STORE).clear();
    await tx.objectStore(METADATA_STORE).clear();
    await tx.done;
  }

  // Clean up when service is destroyed
  destroy() {
    if (this.autoInitializeTimer) {
      clearInterval(this.autoInitializeTimer);
      this.autoInitializeTimer = null;
    }
    if (this.db) {
      this.db.close();
      this.db = null;
    }
    this.isInitialized = false;
    this.encryptionKey = null;
  }
}

export const storageService = new StorageService();
